import { apiClient } from '@/core/api/client';
import { downloadPdf } from './reportApi';
import type { ReadinessBand } from './reportApi';
import type { V2ReportPillar, V2SupportItem, V2Timing } from './v2ReportApi';

/**
 * The advisor's view of an MVP 2 result, after Brooks's advisor report designs:
 * the client's own top 3, each opened to three sections, then what is coming for
 * the client by timing. Only the advisor who sent the assessment can reach it,
 * and only once the client shared.
 */

export type V2HouseholdFact = { label: string; value: string };

/** "Actions for your client" — what the client's own report gave them. */
export type V2AdvisorActions = {
  startHere: string;
  /** The client's workbook button, as their report shows it. */
  workbook: V2SupportItem | null;
  /** The primary bQuest provider; null when it is Advisor-led. */
  referredTo: V2SupportItem | null;
  /** The secondary providers, each with its "when" as the note. */
  then: V2SupportItem[];
};

/** One of the client's top 3. Sections in Kelly's order. */
export type V2AdvisorNeed = {
  rank: number;
  needId: string;
  title: string;
  pillarName: string;
  readinessLabel: string;
  when: V2Timing;
  whyItMatters: string;
  actions: V2AdvisorActions;
  /** Spoken to the client. */
  conversationStarter: string;
};

/** A need under "What's coming"; `clientStep` is "Client will be asked to". */
export type V2ComingNeed = {
  needId: string;
  title: string;
  pillarName: string;
  clientStep: string;
};

/** `guidance` is the client report's caption for the timing. */
export type V2ComingGroup = { when: V2Timing; guidance: string; needs: V2ComingNeed[] };

export type V2AdvisorReport = {
  clientName: string;
  advisorName: string;
  /**
   * Who a supporter answered about — "Mary (parent)" — shown as "About: …"
   * under the client's name. Null without a typed name, and always on SELF.
   */
  about: string | null;
  completedAt: string | null;
  /** When the client shared — the date behind "Shared with consent". */
  sharedAt: string | null;

  overallScore: number;
  category: string;
  categoryLabel: string;
  readinessBands: ReadinessBand[];
  pillars: V2ReportPillar[];

  household: V2HouseholdFact[];
  topPriorities: V2AdvisorNeed[];
  comingGroups: V2ComingGroup[];
  timingNote: string;
};

export async function getAdvisorReport(assessmentId: string): Promise<V2AdvisorReport> {
  const response = await apiClient.get<V2AdvisorReport>(
    `/longevity-assessments/${encodeURIComponent(assessmentId)}/advisor-report`
  );
  return response.data;
}

/**
 * The confidential advisor PDF. Carries the advisor's conversation starters, so the route is the advisor's alone — a client session gets a 404
 * rather than a document.
 */
export const downloadAdvisorReportPdf = (assessmentId: string): Promise<string> =>
  downloadPdf(
    `/longevity-assessments/${encodeURIComponent(assessmentId)}/advisor-report/pdf`,
    'longevity-readiness-advisor-report.pdf'
  );
